import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { errMsg } from "../lib/api";
import { User, Building2, Check } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "private" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await register(form); navigate("/dashboard"); }
    catch (e) { setErr(errMsg(e, "Erreur lors de l'inscription")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-28 pb-20 max-w-md mx-auto px-5">
        <h1 className="text-3xl font-bold text-[#111827]">{t("register")}</h1>

        {step === 0 ? (
          <>
            <p className="text-slate-500 mt-2 mb-6">{t("accountType")}</p>
            <div className="space-y-3">
              {[
                { v: "private", icon: User, title: t("privateOwner"), desc: t("privateDesc") },
                { v: "agency", icon: Building2, title: t("agency"), desc: t("agencyDesc") },
              ].map(({ v, icon: Icon, title, desc }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm({ ...form, role: v })}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition ${form.role === v ? "border-[#1B8F5A] bg-[#1B8F5A]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  data-testid={`role-${v}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${form.role === v ? "bg-[#1B8F5A] text-white" : "bg-slate-100 text-slate-500"}`}>
                    <Icon size={22} />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-[#111827]">{title}</div>
                    <div className="text-sm text-slate-500">{desc}</div>
                  </div>
                  {form.role === v && <Check size={20} className="text-[#1B8F5A]" />}
                </button>
              ))}
            </div>
            <Button onClick={() => setStep(1)} className="w-full mt-6 bg-[#1B8F5A] hover:bg-[#157047] h-12" data-testid="step-continue">Continuer →</Button>
            <p className="text-sm text-slate-600 text-center mt-4">{t("haveAccount")} <Link to="/login" className="text-[#1B8F5A] font-semibold">{t("login")}</Link></p>
          </>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <F label={t("name")}><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" /></F>
            <F label={t("email")}><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" /></F>
            <F label={t("phone")}><Input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone" placeholder="+213..." /></F>
            <F label={t("password")}><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" /></F>
            {err && <div className="text-sm text-red-600">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-[#1B8F5A] hover:bg-[#157047] h-12" data-testid="register-submit">{busy ? "…" : t("register")}</Button>
            <button type="button" onClick={() => setStep(0)} className="text-sm text-slate-500 hover:text-slate-700">← Retour</button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">{label}</label>{children}</div>;
}
